import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Plane, CalendarHeart, Megaphone, BriefcaseMedical,
  ChevronDown, Quote, Check,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLang } from "../i18n";
import { volontariatoTranslations } from "../volontariato-i18n";
import { Logo } from "./Header";
import { LanguageSelector } from "./LanguageSelector";
import { Seo } from "./Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const HERO_IMG =
  "https://images.unsplash.com/photo-1593113646773-028c64a8f1b8?q=80&w=2000&auto=format&fit=crop";

const AREA_ICONS = { field: Plane, events: CalendarHeart, communication: Megaphone, skills: BriefcaseMedical };

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/[0.05] px-5 py-4 text-base text-white placeholder:text-white/35 outline-none transition-all duration-300 focus:border-primary focus:bg-white/[0.08] focus:ring-2 focus:ring-primary/30";

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration: 0.8, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function VolontariatoPage() {
  const { lang } = useLang();
  const v = volontariatoTranslations[lang];
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", surname: "", email: "", phone: "",
    area: "field", availability: "occasional", message: "",
  });
  const [sending, setSending] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const scrollToForm = () =>
    document.getElementById("volontariato-form")?.scrollIntoView({ behavior: "smooth" });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/volunteer`, form);
      toast.success(v.success);
      setForm({ name: "", surname: "", email: "", phone: "", area: "field", availability: "occasional", message: "" });
    } catch {
      toast.error(v.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="volontariato-page">
      <Seo
        path="/volontariato"
        title={lang === "en" ? "Volunteer — You4Mada" : "Volontariato — You4Mada"}
        description={lang === "en"
          ? "Become a You4Mada volunteer: travel to Madagascar, organize events or share your skills. Join us to change lives."
          : "Diventa volontario di You4Mada: parti per il Madagascar, organizza eventi o dona le tue competenze. Unisciti a noi per cambiare vite."}
      />
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#04122e]/70 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 md:px-8">
          <Logo onClick={() => navigate("/")} />
          <div className="flex items-center gap-4">
            <LanguageSelector compact />
            <button
              data-testid="volontariato-back-home"
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-white/60 transition-colors hover:text-white"
            >
              <ArrowLeft size={14} />
              {v.backHome}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden pt-[72px]">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Volunteers working together" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#04132e]/70" />
          <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#04132e] to-transparent" />
        </div>
        <div className="relative mx-auto flex max-w-7xl flex-col justify-end px-5 pb-20 pt-32 md:min-h-[85vh] md:px-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="mb-6 text-[12px] font-semibold uppercase tracking-[0.35em] text-primary"
          >
            {v.kicker}
          </motion.p>
          <h1 className="font-serif text-5xl leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-8xl">
            {v.titleLines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  data-testid={`volontariato-title-line-${i}`}
                  className={`block ${i === v.titleLines.length - 1 ? "italic text-primary" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.25 + i * 0.16, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-white/80 md:text-lg"
            data-testid="volontariato-subtitle"
          >
            {v.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button
              data-testid="volontariato-cta-primary"
              onClick={scrollToForm}
              className="rounded-full bg-primary px-9 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-white shadow-xl shadow-primary/30 transition-all duration-300 hover:-translate-y-1 hover:bg-[#e0562a]"
            >
              {v.ctaPrimary}
            </button>
            <button
              data-testid="volontariato-cta-secondary"
              onClick={() =>
                document.getElementById("volontariato-come")?.scrollIntoView({ behavior: "smooth" })
              }
              className="rounded-full border border-white/25 px-9 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:border-white/60 hover:bg-white/5"
            >
              {v.ctaSecondary}
            </button>
          </motion.div>
        </div>
      </section>

      {/* Come funziona */}
      <section id="volontariato-come" className="py-24 md:py-32" data-testid="volontariato-how">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <p className="text-[12px] font-semibold uppercase tracking-[0.35em] text-primary">
              {v.howLabel}
            </p>
            <h2 className="mt-5 max-w-2xl font-serif text-4xl leading-[1.05] tracking-tight text-white md:text-5xl">
              {v.howTitle}
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-12 md:grid-cols-3 md:gap-8">
            {v.steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.12} className="border-t border-white/10 pt-8">
                <span className="font-serif text-5xl italic leading-none text-primary/80">{s.n}</span>
                <h3 className="mt-6 font-serif text-3xl tracking-tight text-white">{s.title}</h3>
                <p className="mt-4 text-base leading-relaxed text-white/65">{s.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Aree di volontariato */}
      <section className="border-y border-white/10 bg-[#051838] py-24 md:py-32" data-testid="volontariato-areas">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <p className="text-[12px] font-semibold uppercase tracking-[0.35em] text-primary">
              {v.areasLabel}
            </p>
            <h2 className="mt-5 max-w-2xl font-serif text-4xl leading-[1.05] tracking-tight text-white md:text-5xl">
              {v.areasTitle}
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {v.areas.map((r, i) => {
              const Icon = AREA_ICONS[r.key];
              return (
                <Reveal
                  key={r.key}
                  delay={i * 0.1}
                  className="group rounded-3xl border border-white/10 bg-white/[0.04] p-8 transition-all duration-500 hover:-translate-y-2 hover:border-primary/50"
                >
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-primary/15 text-primary transition-colors duration-500 group-hover:bg-primary group-hover:text-white">
                    <Icon size={22} strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-6 font-serif text-2xl tracking-tight text-white">{r.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{r.text}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Galleria volontari */}
      <section className="py-24 md:py-32" data-testid="volontariato-gallery">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <p className="text-[12px] font-semibold uppercase tracking-[0.35em] text-primary">
              {v.galleryLabel}
            </p>
            <h2 className="mt-5 max-w-2xl font-serif text-4xl leading-[1.05] tracking-tight text-white md:text-5xl">
              {v.galleryTitle}
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-4 md:grid-rows-2">
            {[
              {
                src: "https://images.unsplash.com/photo-1560252829-804f1aedf1be?q=80&w=1600&auto=format&fit=crop",
                alt: lang === "it" ? "Volontari che lavorano insieme" : "Volunteers working together",
                span: "col-span-2 md:row-span-2",
                cls: "aspect-[4/3] md:aspect-auto md:h-full",
              },
              {
                src: "https://images.unsplash.com/photo-1559027615-cd4628902d4a?q=80&w=1200&auto=format&fit=crop",
                alt: lang === "it" ? "Volontaria con bambini" : "Volunteer with children",
                span: "",
                cls: "aspect-square",
              },
              {
                src: "https://images.unsplash.com/photo-1509099836639-18ba1795216d?q=80&w=1200&auto=format&fit=crop",
                alt: lang === "it" ? "Mani unite di volontari" : "Volunteers joining hands",
                span: "",
                cls: "aspect-square",
              },
              {
                src: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=1200&auto=format&fit=crop",
                alt: lang === "it" ? "Aiuto sul campo" : "Help in the field",
                span: "",
                cls: "aspect-square",
              },
              {
                src: "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?q=80&w=1200&auto=format&fit=crop",
                alt: lang === "it" ? "Gruppo di volontari" : "Group of volunteers",
                span: "",
                cls: "aspect-square",
              },
            ].map((img, i) => (
              <Reveal key={i} delay={i * 0.08} className={img.span}>
                <div
                  className={`group relative overflow-hidden rounded-2xl border border-white/10 ${img.cls}`}
                  data-testid={`volontariato-photo-${i}`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-[#04132e]/10 transition-colors duration-500 group-hover:bg-transparent" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Modulo */}
      <section id="volontariato-form" className="py-24 md:py-32" data-testid="volontariato-form-section">
        <div className="mx-auto grid max-w-7xl gap-16 px-5 md:px-8 lg:grid-cols-2">
          <Reveal>
            <p className="text-[12px] font-semibold uppercase tracking-[0.35em] text-primary">
              {v.formLabel}
            </p>
            <h2 className="mt-5 font-serif text-4xl leading-[1.05] tracking-tight text-white md:text-6xl">
              {v.formTitle}
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/65 md:text-lg">
              {v.formSub}
            </p>
            <div className="mt-10 space-y-4">
              {v.testimonials.map((tst, i) => (
                <div key={i} className="flex gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-6" data-testid={`volontariato-testimonial-${i}`}>
                  <Quote size={20} className="shrink-0 text-primary" />
                  <div>
                    <p className="font-serif text-lg italic leading-relaxed text-white/80">
                      {tst.quote}
                    </p>
                    <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
                      {tst.author}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form
              data-testid="volontariato-application-form"
              onSubmit={submit}
              className="flex flex-col gap-5 rounded-3xl border border-white/10 bg-white/[0.04] p-8 md:p-10"
            >
              <div className="grid grid-cols-2 gap-4">
                <input
                  data-testid="volontariato-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={v.name}
                  className={inputCls}
                />
                <input
                  data-testid="volontariato-surname-input"
                  required
                  value={form.surname}
                  onChange={(e) => setForm({ ...form, surname: e.target.value })}
                  placeholder={v.surname}
                  className={inputCls}
                />
              </div>
              <input
                data-testid="volontariato-email-input"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={v.email}
                className={inputCls}
              />
              <input
                data-testid="volontariato-phone-input"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder={v.phone}
                className={inputCls}
              />

              <div>
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
                  {v.areaLabel}
                </p>
                <div className="flex flex-wrap gap-2" data-testid="volontariato-area-toggle">
                  {v.areaOptions.map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      data-testid={`volontariato-area-${o.v}`}
                      onClick={() => setForm({ ...form, area: o.v })}
                      className={`flex items-center gap-2 rounded-full border px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.1em] transition-all duration-300 ${
                        form.area === o.v
                          ? "border-primary bg-primary/15 text-white"
                          : "border-white/15 bg-white/[0.04] text-white/60 hover:border-white/40"
                      }`}
                    >
                      {form.area === o.v && <Check size={13} className="text-primary" />}
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
                  {v.availabilityLabel}
                </p>
                <div className="grid grid-cols-3 gap-3" data-testid="volontariato-availability-toggle">
                  {v.availabilityOptions.map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      data-testid={`volontariato-availability-${o.v}`}
                      onClick={() => setForm({ ...form, availability: o.v })}
                      className={`rounded-2xl border p-4 text-center text-[12px] font-semibold uppercase tracking-[0.08em] transition-all duration-300 ${
                        form.availability === o.v
                          ? "border-primary bg-primary/15 text-white"
                          : "border-white/15 bg-white/[0.04] text-white/60 hover:border-white/40"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                data-testid="volontariato-message-input"
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={v.message}
                className={`${inputCls} resize-none`}
              />

              <button
                data-testid="volontariato-submit-button"
                type="submit"
                disabled={sending}
                className="mt-2 rounded-full bg-primary px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-white shadow-lg shadow-primary/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#e0562a] disabled:translate-y-0 disabled:opacity-60"
              >
                {sending ? v.sending : v.submit}
              </button>
              <p className="text-center text-[12px] leading-relaxed text-white/40">{v.note}</p>
            </form>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/10 py-24 md:py-32" data-testid="volontariato-faq">
        <div className="mx-auto max-w-4xl px-5 md:px-8">
          <Reveal className="text-center">
            <p className="text-[12px] font-semibold uppercase tracking-[0.35em] text-primary">
              {v.faqLabel}
            </p>
            <h2 className="mt-5 font-serif text-4xl leading-[1.05] tracking-tight text-white md:text-5xl">
              {v.faqTitle}
            </h2>
          </Reveal>
          <div className="mt-14 border-t border-white/10">
            {v.faq.map((f, i) => (
              <div key={i} className="border-b border-white/10">
                <button
                  data-testid={`volontariato-faq-${i}`}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="flex w-full items-center justify-between gap-6 py-7 text-left"
                >
                  <span className="font-serif text-xl tracking-tight text-white md:text-2xl">
                    {f.q}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`shrink-0 text-primary transition-transform duration-300 ${
                      openFaq === i ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <p className="max-w-2xl pb-7 text-base leading-relaxed text-white/65">{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
